import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Tooltip from "@mui/material/Tooltip";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { InfoCircle } from "react-bootstrap-icons";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import "./styles/create-new-client.css";

// Custom Input Field component specific to CreateClient component
const InputField = ({
  label,
  state,
  valueKey,
  value,
  setValue,
  tooltipMessage,
  inputType,
}) => {
  const { handleFormatPhone, formatFaxId, formatTaxId } =
    useContext(DashboardContext);

  const handleTaxIdChange = (e) => {
    // Only allow numbers and dashes
    let value = e.target.value;
    let formattedValue = formatTaxId(value);

    setValue({ ...state, [valueKey]: formattedValue });
  };

  const handleFaxChange = (e) => {
    // Only allow numbers and dashes
    let value = e.target.value;
    let formattedValue = formatFaxId(value);

    setValue({ ...state, [valueKey]: formattedValue });
  };

  // Phone number input handler
  const handlePhoneChange = (e) => {
    const formattedPhone = handleFormatPhone(e.target.value);
    setValue({ ...state, [valueKey]: formattedPhone });
  };

  // Text input handler
  const handleTextChange = (e) => {
    let value = e.target.value;
    if (inputType === "state") {
      value = value.slice(0, 2).toUpperCase();
      setValue({ ...state, [valueKey]: value });
      return;
    }
    let formattedValue = value.replace(/[^a-zA-Z0-9- ,'.& #]/g, "");

    setValue({ ...state, [valueKey]: formattedValue });
  };

  // Event handler object to be used to determine which handler to use the OutlinedInput onChange event
  const eventHandlers = {
    phone: handlePhoneChange,
    text: handleTextChange,
    fax: handleFaxChange,
    taxId: handleTaxIdChange,
    state: handleTextChange,
  };

  return (
    <div className="inputs-container">
      <div className="input-info-container">
        <FormControl
          variant="outlined"
          id="input-form"
          className="input-form"
          classes={{
            root: "input-form-root",
          }}
        >
          <InputLabel
            id="form-label"
            htmlFor="input-text"
            classes={{
              shrink: "input-label-shrink",
            }}
          >
            {`Add ${label}`}
          </InputLabel>
          <OutlinedInput
            type={inputType}
            id="input-text"
            value={value}
            classes={{
              root: "input-text-root",
              notchedOutline: "input-text-notched-outline",
              input: "input-text-input",
            }}
            autoComplete="off"
            endAdornment={
              <Tooltip
                id="clear-btn-tooltip"
                classes={{
                  tooltip: "tooltip-clear-btn",
                  popper: "popper-clear-btn",
                  tooltipPlacementTop: "tooltip-top",
                }}
                placement="top"
                title={value ? "Clear Field" : ""}
              >
                <InputAdornment
                  className="input-clear-adornment"
                  id="clear-adornment"
                  position="end"
                  style={{
                    visibility: value ? "visible" : "hidden",
                  }}
                >
                  <IconButton
                    style={{
                      pointerEvents: value ? "auto" : "none",
                      cursor: value ? "pointer" : "default",
                    }}
                    onClick={() => {
                      setValue({
                        ...state,
                        [valueKey]: "",
                      });
                    }}
                    edge="end"
                    id="clear-input"
                    disabled={value ? false : true}
                  >
                    <CloseIcon className="close-icon" id="close-icon" />
                  </IconButton>
                </InputAdornment>
              </Tooltip>
            }
            onChange={eventHandlers[inputType]}
          />
        </FormControl>
        <Tooltip
          id="tf-tooltip"
          classes={{
            tooltip: "text-field-tooltip",
            popper: "text-field-popper",
            arrow: "text-field-arrow",
            tooltipPlacementLeft: "text-field-right",
          }}
          placement="right"
          arrow
          sx={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
          title={tooltipMessage}
        >
          <InfoCircle
            className="text-info-circ client-info-circ"
            color="white"
            size={30}
          />
        </Tooltip>
      </div>
    </div>
  );
};

// Component to display a list of existing clients to allow for editing an already existing client
const ExistingClients = ({
  existingClientsData,
  setExistingClient,
  mode,
  setMode,
}) => {
  const [sortedClients, setSortedClients] = useState([]);

  // Sorting the existing clients by name
  useEffect(() => {
    if (existingClientsData) {
      setSortedClients(
        existingClientsData.sort((a, b) => a.name.localeCompare(b.name)),
      );
    }
  }, [existingClientsData]);

  return (
    <div className={`existing-clients-container`}>
      <header id="existing-client-header">
        <h1 id="existing-clients-header">Existing Clients</h1>
      </header>
      <div className="clients-list">
        {existingClientsData.map((client) => (
          <div key={client.id} className="client-item">
            <p className="client-data" id="name">
              {client.name}
            </p>
            {client.shortName && client.shortName !== client.name ? (
              <p className="client-data">({client.shortName})</p>
            ) : null}
            <div className="existing-clients-btns">
              <Button
                variant="contained"
                id="edit-client-btn"
                className="edit-client-btn"
                onClick={() => {
                  setExistingClient({
                    id: client.id,
                    name: client.name,
                    shortName: client.shortName,
                    address: client.address,
                    city: client.city,
                    state: client.state,
                    zipCode: client.zipCode,
                    phone: client.phone,
                    companyCode: client.companyCode,
                    fax: client.fax,
                    taxId: client.taxId,
                  });
                  setMode("update");
                }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                id="delete-client-btn"
                className="edit-client-btn"
                onClick={() => {
                  console.log("Delete client: ", client);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main CreateClient component
const CreateClient = () => {
  const {
    setHeading,
    setOpenSnackbar,
    setSnackbarSeverity,
    setSnackbarMessage,
    queryClient,
  } = useContext(DashboardContext);

  useEffect(() => {
    setHeading("Create/Edit Clients");
  }, []);

  const [mode, setMode] = useState("create");
  // State for new client data
  const [newClient, setNewClient] = useState({
    name: "",
    shortName: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
    fax: "",
    taxId: "",
  });
  // State for existing client data
  const [existingClient, setExistingClient] = useState({
    name: "",
    shortName: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
    fax: "",
    taxId: "",
    companyCode: "",
  });

  const nevadaZipCodes = [
    "89001",
    "89002",
    "89003",
    "89004",
    "89005",
    "89007",
    "89008",
    "89010",
    "89011",
    "89012",
    "89013",
    "89014",
    "89015",
    "89017",
    "89018",
    "89019",
    "89020",
    "89021",
    "89022",
    "89025",
    "89026",
    "89027",
    "89029",
    "89030",
    "89031",
    "89032",
    "89034",
    "89039",
    "89040",
    "89042",
    "89043",
    "89044",
    "89045",
    "89046",
    "89047",
    "89048",
    "89049",
    "89052",
    "89054",
    "89060",
    "89061",
    "89070",
    "89074",
    "89081",
    "89084",
    "89085",
    "89086",
    "89101",
    "89102",
    "89103",
    "89104",
    "89106",
    "89107",
    "89108",
    "89109",
    "89110",
    "89113",
    "89115",
    "89117",
    "89118",
    "89119",
    "89120",
    "89121",
    "89122",
    "89123",
    "89124",
    "89128",
    "89129",
    "89130",
    "89131",
    "89134",
    "89135",
    "89138",
    "89139",
    "89141",
    "89142",
    "89143",
    "89144",
    "89145",
    "89146",
    "89147",
    "89148",
    "89149",
    "89156",
    "89158",
    "89161",
    "89166",
    "89169",
    "89178",
    "89179",
    "89183",
    "89191",
    "89301",
    "89310",
    "89311",
    "89314",
    "89315",
    "89316",
    "89317",
    "89318",
    "89319",
    "89402",
    "89403",
    "89404",
    "89405",
    "89406",
    "89408",
    "89409",
    "89410",
    "89411",
    "89412",
    "89413",
    "89414",
    "89415",
    "89418",
    "89419",
    "89420",
    "89421",
    "89422",
    "89423",
    "89424",
    "89425",
    "89426",
    "89427",
    "89428",
    "89429",
    "89430",
    "89431",
    "89433",
    "89434",
    "89436",
    "89437",
    "89438",
    "89439",
    "89440",
    "89441",
    "89442",
    "89444",
    "89445",
    "89446",
    "89447",
    "89448",
    "89449",
    "89450",
    "89451",
    "89460",
    "89496",
    "89501",
    "89502",
    "89503",
    "89506",
    "89508",
    "89509",
    "89510",
    "89511",
    "89512",
    "89519",
    "89521",
    "89523",
    "89557",
    "89701",
    "89703",
    "89704",
    "89705",
    "89706",
    "89801",
    "89815",
    "89820",
    "89821",
    "89822",
    "89823",
    "89825",
    "89826",
    "89828",
    "89830",
    "89831",
    "89832",
    "89833",
    "89834",
    "89835",
    "89883",
  ];

  // Query to get existing clients
  const { data: existingClientsData, isFetched } = useQuery({
    queryKey: ["existingClients"],
    queryFn: async () => {
      const res = await axios.get("/api/clients/", {
        withCredentials: true,
      });

      if (res.status === 200 && res.data.length > 0) {
        let returnData = res.data.sort((a, b) => a.name.localeCompare(b.name));

        return returnData;
      }

      return res.data;
    },
  });

  // Mutation to create a new client
  const createClientMutation = useMutation({
    mutationFn: async (newClient) => {
      let newData = {};
      if (newClient.shortName === "") {
        newData = {
          ...newClient,
          shortName: newClient.name,
        };
        setNewClient(newData);
      } else {
        newData = newClient;
      }
      const res = await axios.post("/api/client/new/", newData, {
        withCredentials: true,
      });
      console.log("Response: ", res);
      return res;
    },
    onError: (error) => {
      setSnackbarSeverity(() => "error");
      setSnackbarMessage(() => `Error creating ${newClient.shortName}!`);
      setOpenSnackbar(true);
    },
    onSuccess: (data) => {
      setSnackbarSeverity(() => "success");
      setSnackbarMessage(
        () => `${newClient.shortName} has successfully been created!`,
      );
      setOpenSnackbar(true);
      setNewClient({
        name: "",
        shortName: "",
        address: "",
        city: "",
        zipCode: "",
        state: "",
        phone: "",
        fax: "",
        taxId: "",
      });
      queryClient.invalidateQueries({
        queryKey: ["existingClients"],
      });
    },
  });

  // Mutation to update an existing client
  const updateClientMutation = useMutation({
    mutationFn: async (existingClient) => {
      const res = await axios.patch(
        `/api/client/update/${existingClient.id}/`,
        existingClient,
        {
          withCredentials: true,
        },
      );
      return res;
    },
    onError: (error) => {
      setSnackbarSeverity(() => "error");
      setSnackbarMessage(
        () => `Error updating ${existingClient.shortName}'s information!`,
      );
      setOpenSnackbar(true);
    },
    onSuccess: (data) => {
      setSnackbarSeverity(() => "success");
      setSnackbarMessage(
        () =>
          `${existingClient.shortName}'s information has successfully been updated!`,
      );
      setOpenSnackbar(true);
      setExistingClient({
        name: "",
        shortName: "",
        address: "",
        city: "",
        zipCode: "",
        state: "",
        phone: "",
        companyCode: "",
        fax: "",
        taxId: "",
      });
      setMode("create");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["existingClients"] });
    },
  });

  useEffect(() => {
    if (newClient.zipCode && newClient.zipCode.length === 5) {
      if (nevadaZipCodes.includes(newClient.zipCode)) {
        setNewClient({ ...newClient, state: "NV" });
      }
    }
  }, [newClient.zipCode]);

  return (
    <>
      <div className={`create-client-container`}>
        <Box id={mode === "create" ? "create-client" : "edit-client"}>
          <header id="client-header">
            <h1 id="create-client-header">
              {mode === "create" ? "Create New Client" : "Edit Client"}
            </h1>
          </header>
          <div className="create-client-form-container">
            <InputField
              label="Client Name"
              state={mode === "create" ? newClient : existingClient}
              valueKey="name"
              value={mode === "create" ? newClient.name : existingClient.name}
              setValue={mode === "create" ? setNewClient : setExistingClient}
              tooltipMessage="Enter the full name of the client. (Greystone Nevada, LLC., PN II, Inc., etc.)"
              inputType="text"
            />
            <InputField
              label="Short Name"
              state={mode === "create" ? newClient : existingClient}
              valueKey="shortName"
              value={
                mode === "create"
                  ? newClient.shortName
                  : existingClient.shortName
              }
              setValue={mode === "create" ? setNewClient : setExistingClient}
              tooltipMessage="Enter an abbreviated name for the client. (Lennar, Pulte, etc.)"
              inputType="text"
            />
            <InputField
              label="Address"
              state={mode === "create" ? newClient : existingClient}
              valueKey="address"
              value={
                mode === "create" ? newClient.address : existingClient.address
              }
              setValue={mode === "create" ? setNewClient : setExistingClient}
              tooltipMessage="Client's physical address. (1234 Main St.)"
              inputType="text"
            />
            <InputField
              label="City"
              state={mode === "create" ? newClient : existingClient}
              valueKey="city"
              value={mode === "create" ? newClient.city : existingClient.city}
              setValue={mode === "create" ? setNewClient : setExistingClient}
              tooltipMessage="City where the client is located"
              inputType="text"
            />
            <InputField
              label="Zip Code"
              state={mode === "create" ? newClient : existingClient}
              valueKey="zipCode"
              value={
                mode === "create" ? newClient.zipCode : existingClient.zipCode
              }
              setValue={mode === "create" ? setNewClient : setExistingClient}
              tooltipMessage="Client's zip code"
              inputType="text"
            />
            <InputField
              label="State"
              state={mode === "create" ? newClient : existingClient}
              valueKey="state"
              value={mode === "create" ? newClient.state : existingClient.state}
              setValue={mode === "create" ? setNewClient : setExistingClient}
              tooltipMessage="NV, AZ, CA, etc."
              inputType="state"
            />
            <InputField
              label="Phone Number"
              state={mode === "create" ? newClient : existingClient}
              valueKey="phone"
              value={mode === "create" ? newClient.phone : existingClient.phone}
              setValue={mode === "create" ? setNewClient : setExistingClient}
              tooltipMessage="Client's phone number"
              inputType="phone"
            />
            <InputField
              label="Fax Number"
              state={mode === "create" ? newClient : existingClient}
              valueKey="fax"
              value={mode === "create" ? newClient.fax : existingClient.fax}
              setValue={mode === "create" ? setNewClient : setExistingClient}
              tooltipMessage="Client's fax number"
              inputType="fax"
            />
            <InputField
              label="Tax ID (TIN)"
              state={mode === "create" ? newClient : existingClient}
              valueKey="taxId"
              value={mode === "create" ? newClient.taxId : existingClient.taxId}
              setValue={mode === "create" ? setNewClient : setExistingClient}
              tooltipMessage="Client's Tax Identification Number"
              inputType="taxId"
            />
            {mode === "update" && (
              <InputField
                label="Company Code"
                state={existingClient}
                valueKey="companyCode"
                value={existingClient.companyCode}
                setValue={setExistingClient}
                tooltipMessage="Client's companyCode"
                inputType="text"
              />
            )}
          </div>
          {mode === "create" && (
            <Button
              type="submit"
              variant="contained"
              id="submit-new-client-btn"
              className="submit-new-client-btn"
              disabled={
                !(
                  newClient.name &&
                  newClient.address &&
                  newClient.city &&
                  newClient.state &&
                  newClient.zipCode &&
                  newClient.phone
                )
              }
              onClick={async (e) => {
                e.preventDefault();
                if (
                  newClient.name &&
                  newClient.address &&
                  newClient.city &&
                  newClient.state &&
                  newClient.zipCode &&
                  newClient.phone
                ) {
                  await createClientMutation.mutateAsync(newClient);
                }
              }}
            >
              Create
            </Button>
          )}
          {mode === "update" && (
            <div className="update-client-btns">
              <Button
                variant="contained"
                id="update-client-btn"
                className="update-client-btn"
                onClick={async (e) => {
                  e.preventDefault();
                  await updateClientMutation.mutateAsync(existingClient);
                }}
              >
                Update
              </Button>
              <Button
                variant="contained"
                id="cancel-edit-client-btn"
                className="cancel-edit-client-btn"
                onClick={() => {
                  setMode("create");
                  setExistingClient({
                    name: "",
                    shortName: "",
                    address: "",
                    city: "",
                    zipCode: "",
                    state: "",
                    phone: "",
                    companyCode: "",
                    fax: "",
                    taxId: "",
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </Box>
        {isFetched && existingClientsData && existingClientsData.length > 0 && (
          <ExistingClients
            existingClientsData={existingClientsData}
            setExistingClient={setExistingClient}
            mode={mode}
            setMode={setMode}
          />
        )}
      </div>
    </>
  );
};

export default CreateClient;
